import { useState } from "react";
import type { V2Bag } from "@/features/applications/api/applications.api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Notice } from "@/components/Notice";
import { formatDateTime } from "@/utils/dates";
import { rateExceptionCovers, rateExceptionOf } from "../model";
import { PRICING_RATE_FLOORS, IC_PER_TXN_FLOOR } from "../pricing";
import { useReadOnly } from "../readonly";

const MAX_TEXT = 500;

export interface RateExceptionActions {
  canDecide: boolean;
  busy: boolean;
  onRequest: (reason: string) => Promise<boolean>;
  onDecide: (approve: boolean, note: string | null) => Promise<boolean>;
}

export function isBelowFloor(bag: V2Bag): boolean {
  const program = typeof bag.pricingProgram === "string" ? bag.pricingProgram : "";
  const floor = PRICING_RATE_FLOORS[program];
  const rate = bag.pricingRatePct;
  if (floor !== undefined && typeof rate === "number" && rate < floor) return true;
  const fee = bag.pricingPerTxnFee;
  return program === "Interchange Plus" && typeof fee === "number" && fee < IC_PER_TXN_FLOOR;
}

export function RateExceptionCard({ bag, actions }: { bag: V2Bag; actions?: RateExceptionActions }) {
  const readOnly = useReadOnly();
  const [asking, setAsking] = useState(false);
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");

  const exception = rateExceptionOf(bag);
  const current = exception && rateExceptionCovers(bag, exception) ? exception : null;
  const rate = typeof bag.pricingRatePct === "number" ? `${bag.pricingRatePct}%` : "this rate";
  const by = (name?: string | null, at?: string | null) => (
    <span className="text-xs opacity-80">
      {name}{at ? ` · ${formatDateTime(at)}` : ""}
    </span>
  );
  const quoted = (text?: string | null) => (text ? <span className="block italic">“{text}”</span> : null);

  if (current?.status === "Approved") {
    return (
      <Notice tone="success" className="mt-3 px-3 py-2">
        ✓ <b>Exception approved</b> for {current.ratePct}% {current.program}. {by(current.decidedBy, current.decidedAt)}
        {quoted(current.note)}
      </Notice>
    );
  }

  if (current?.status === "Declined") {
    return (
      <Notice tone="error" className="mt-3 px-3 py-2">
        ✕ <b>Exception declined</b>. Change the rate to continue. {by(current.decidedBy, current.decidedAt)}
        {quoted(current.note)}
      </Notice>
    );
  }

  if (current?.status === "Pending") {
    return (
      <Notice tone="warn" className="mt-3 px-3 py-2">
        <div>
          ⏳ <b>Rate exception requested</b> for {current.ratePct}% {current.program} · waiting on an admin.{" "}
          {by(current.requestedBy, current.requestedAt)}
        </div>
        <div className="mt-1"><b>Why:</b> {current.reason}</div>
        {actions?.canDecide ? (
          <div className="mt-3 space-y-2">
            <Textarea
              value={note}
              maxLength={MAX_TEXT}
              rows={2}
              placeholder="Note to the agent (optional)"
              onChange={(e) => setNote(e.target.value)}
            />
            <div className="flex flex-wrap gap-2">
              <Button type="button" size="sm" disabled={actions.busy}
                onClick={async () => { if (await actions.onDecide(true, note.trim() || null)) setNote(""); }}>
                Approve {current.ratePct}%
              </Button>
              <Button type="button" size="sm" variant="outline" disabled={actions.busy}
                onClick={async () => { if (await actions.onDecide(false, note.trim() || null)) setNote(""); }}>
                Decline
              </Button>
            </div>
          </div>
        ) : (
          <p className="mt-1 text-xs">You can still submit it to back office. It can't be sent for processing until an admin approves.</p>
        )}
      </Notice>
    );
  }

  if (!isBelowFloor(bag) || readOnly || !actions) return null;

  return (
    <div className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
      {asking ? (
        <div className="space-y-2">
          <label className="block text-xs font-semibold uppercase tracking-wide" htmlFor="rate-exception-reason">
            Why does this merchant need {rate}?
          </label>
          <Textarea
            id="rate-exception-reason"
            value={reason}
            maxLength={MAX_TEXT}
            rows={3}
            className="bg-white text-slate-900"
            placeholder="e.g. Competing offer from their current processor at 2.7%, $80k/month volume"
            onChange={(e) => setReason(e.target.value)}
          />
          <div className="flex flex-wrap gap-2">
            <Button type="button" size="sm" disabled={actions.busy || !reason.trim()}
              onClick={async () => {
                if (await actions.onRequest(reason.trim())) { setAsking(false); setReason(""); }
              }}>
              Send request
            </Button>
            <Button type="button" size="sm" variant="outline" onClick={() => setAsking(false)}>Cancel</Button>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span>Need a lower rate for this merchant? Ask an admin to approve it.</span>
          <Button type="button" size="sm" variant="outline" className="bg-white" onClick={() => setAsking(true)}>
            Request an exception
          </Button>
        </div>
      )}
    </div>
  );
}
